import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { submitQuickOrder } from '../api/orders';
import { Product, ProductVariant } from '../api/products';
import { validateMoroccanPhone } from '../utils/phoneValidator';
import { useSettings } from '../context/SettingsContext';

import { useCart } from '../context/CartContext';

interface InlineOrderFormProps {
    product: Product;
    variant?: ProductVariant | null;
    quantity: number;
}

const InlineOrderForm: React.FC<InlineOrderFormProps> = ({ product, variant, quantity }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getSetting } = useSettings();
    const { addToCart } = useCart();
    const isRTL = i18n.dir() === 'rtl';

    const [formData, setFormData] = useState({ name: '', phone: '', city: '', address: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Calculate prices
    const unitPrice = variant ? parseFloat(variant.price) : (product.discount_price || product.price);
    const totalPrice = unitPrice * quantity;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (error) setError(null);
    };

    const validateForm = () => {
        if (!formData.name || !formData.phone || !formData.city || !formData.address) {
            setError(t('quickOrderForm.fillFormFirst', 'Please fill all fields first'));
            return false;
        }

        if (!validateMoroccanPhone(formData.phone)) {
            setError(t('validation.invalidPhone', 'Invalid phone number'));
            return false;
        }

        return true;
    };

    const handleDirectSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;

        setLoading(true);
        try {
            await submitQuickOrder({
                product_id: product.id,
                qty: quantity,
                product_variant_id: variant?.id,
                ...formData
            });
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                setFormData({ name: '', phone: '', city: '', address: '' });
                navigate('/thank_you');
            }, 1500);
        } catch (err) {
            setError(t('quickOrderForm.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="bg-white rounded-3xl p-8 shadow-soft text-center h-full flex flex-col items-center justify-center min-h-[400px]">
                <div className="relative mx-auto w-24 h-24 mb-8">
                    {/* Outer Ring Animation */}
                    <motion.div
                        className="absolute inset-0 border-4 border-primary/20 rounded-full"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1.2, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                    />
                    {/* Success Icon Animation */}
                    <motion.div
                        className="relative flex items-center justify-center w-full h-full bg-primary/10 rounded-full"
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                        <svg className="h-12 w-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <motion.path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            />
                        </svg>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {formData.name
                            ? t('quickOrderForm.processingName', { name: formData.name })
                            : t('quickOrderForm.processingGeneral')}
                    </h3>
                    <div className="flex justify-center gap-1 mt-4">
                        {[0, 1, 2].map((i) => (
                            <motion.div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full"
                                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
                                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div id="inline-order-form" className="relative bg-white rounded-[2rem] p-6 md:p-8 shadow-2xl border-4 border-primary/20 mb-8 mt-12 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative z-10">
                <div className="mb-8 text-center pt-2">
                    <h3 className="text-3xl md:text-4xl font-black text-dark mb-2 tracking-tight">
                        {t('common.quickOrder')}
                    </h3>
                    <p className="text-neutral-500 font-medium text-sm md:text-base">
                        {t('quickOrderForm.subtitle', 'Remplissez ce formulaire et payez à la livraison !')}
                    </p>
                </div>

                <form onSubmit={handleDirectSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.fullName')}
                            </label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.enterName')}
                            />
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.phone')}
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all`}
                                placeholder={t('quickOrderForm.phonePlaceholder')}
                                dir="ltr"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.city')}
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                value={formData.city}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.cityPlaceholder')}
                            />
                        </div>

                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.address')}
                            </label>
                            <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.addressPlaceholder')}
                            />
                        </div>
                    </div>

                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                            className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg border border-red-100"
                        >
                            {error}
                        </motion.div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center gap-2 rounded-xl border border-transparent bg-primary px-6 py-4 text-base font-bold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                        >
                            {loading ? (
                                t('common.loading')
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('common.quickOrder')}
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={() => addToCart(product, quantity, variant)}
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center gap-2 rounded-xl border-2 border-primary bg-white px-6 py-4 text-base font-bold text-primary hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {t('common.addToCart')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InlineOrderForm;
